import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface EducationCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface EducationModule {
  id: string;
  title: string;
  description: string | null;
  content_type: string;
  duration_minutes: number;
  is_required: boolean;
  points: number;
  category_id: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ModuleSlide {
  id: string;
  module_id: string;
  title: string;
  content: string | null;
  slide_type: string;
  sort_order: number;
  created_at: string;
}

export interface QuizQuestion {
  id: string;
  module_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  difficulty_level: string | null;
  points: number;
  time_limit_seconds: number | null;
  sort_order: number;
  created_at: string;
}

export interface QuizAnswer {
  question_id: string;
  correct_answer: string;
  explanation: string | null;
  feedback_correct: string | null;
  feedback_incorrect: string | null;
}

export interface UserProgress {
  id: string;
  user_id: string;
  module_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress_percentage: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuizAttempt {
  id: string;
  user_id: string;
  module_id: string;
  score: number;
  total_questions: number;
  passed: boolean;
  attempt_number: number;
  created_at: string;
}

// Fetch education categories
export const useEducationCategories = () => {
  return useQuery({
    queryKey: ['education-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('education_categories')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as EducationCategory[];
    },
  });
};

// Fetch modules by category
export const useEducationModules = (categoryId?: string) => {
  return useQuery({
    queryKey: ['education-modules', categoryId],
    queryFn: async () => {
      let query = supabase
        .from('education_modules')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as EducationModule[];
    },
  });
};

// Fetch slides for a module
export const useModuleSlides = (moduleId: string) => {
  return useQuery({
    queryKey: ['module-slides', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_slides')
        .select('*')
        .eq('module_id', moduleId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as ModuleSlide[];
    },
    enabled: !!moduleId,
  });
};

// Fetch quiz questions for a module (secure - no answers exposed)
export const useQuizQuestions = (moduleId: string) => {
  return useQuery({
    queryKey: ['quiz-questions', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_quiz_questions_for_user', {
        module_uuid: moduleId
      });
      
      if (error) throw error;
      return data as QuizQuestion[];
    },
    enabled: !!moduleId,
  });
};

// Fetch quiz answers after submission (secure - only for completed attempts)
export const useQuizAnswers = (moduleId: string, attemptId: string | null) => {
  return useQuery({
    queryKey: ['quiz-answers', moduleId, attemptId],
    queryFn: async () => {
      if (!attemptId) return [];
      
      const { data, error } = await supabase.rpc('get_quiz_answers', {
        module_uuid: moduleId,
        user_quiz_attempt_id: attemptId
      });
      
      if (error) throw error;
      return data as QuizAnswer[];
    },
    enabled: !!moduleId && !!attemptId,
  });
};

// Fetch user progress
export const useUserProgress = (userId?: string) => {
  return useQuery({
    queryKey: ['user-progress', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId);
      
      if (error) throw error;
      return data as UserProgress[];
    },
    enabled: !!userId,
  });
};

// Update user progress
export const useUpdateProgress = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (progress: Partial<UserProgress> & { user_id: string; module_id: string }) => {
      const { data, error } = await supabase
        .from('user_progress')
        .upsert(progress, { onConflict: 'user_id,module_id' })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-progress'] });
    },
  });
};

// Submit quiz attempt
export const useSubmitQuizAttempt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (attempt: Omit<QuizAttempt, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('quiz_attempts')
        .insert(attempt)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-attempts'] });
    },
  });
};